import dayjs, { Dayjs } from 'dayjs';
import { getSmartScheduleDurationMinutes, toSmartScheduleKst } from './date';
import {
    NormalizedSmartScheduleData,
    NormalizeSmartScheduleInput,
    SmartScheduleCoach,
    SmartScheduleEvent,
    SmartScheduleKind,
    SmartScheduleMember,
    SmartScheduleStatus,
} from './types';

type Source = 'range' | 'group' | 'opportunity';

type PendingEvent = SmartScheduleEvent & {
    source: Source;
    sourcePriority: number;
};

const SOURCE_PRIORITY: Record<Source, number> = {
    range: 1,
    opportunity: 2,
    group: 3,
};

const GROUP_CLASS_TYPES = new Set(['GROUP', 'PRIVATE_1ON2', 'PRIVATE_1ON3']);
const CANCELED_VALUES = new Set(['CANCELLED', 'CANCELED', 'CANCEL', 'DELETED', 'REJECTED']);
const NO_SHOW_VALUES = new Set(['NOSHOW', 'NO_SHOW', 'NO-SHOW']);

const asString = (value: unknown): string => (value === undefined || value === null ? '' : String(value));

const firstString = (...values: unknown[]): string => {
    const value = values.find((candidate) => typeof candidate === 'string' && candidate.trim());
    return typeof value === 'string' ? value.trim() : '';
};

const firstFiniteNumber = (...values: unknown[]): number | undefined => {
    for (const value of values) {
        const number = typeof value === 'string' && value.trim() ? Number(value) : value;
        if (typeof number === 'number' && Number.isFinite(number)) return number;
    }
    return undefined;
};

const getFixedClassCapacity = (classType: unknown): number | undefined => {
    const normalizedClassType = asString(classType).toUpperCase();
    if (normalizedClassType === 'PRIVATE_1ON2') return 2;
    if (normalizedClassType === 'PRIVATE_1ON3') return 3;
    return undefined;
};

const getObjectId = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return asString(value);
    return asString(value._id || value.id || value.value || value.code);
};

const unwrapCollection = (value: any): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value !== 'object') return [];

    const candidates = [
        value.results,
        value.schedules,
        value.sessions,
        value.opportunities,
        value.coaches,
        value.dailySchedules,
        value.data,
    ];
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
        if (candidate && typeof candidate === 'object') {
            const nested = unwrapCollection(candidate);
            if (nested.length) return nested;
        }
    }
    return [];
};

const flattenTimedItems = (value: any): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.flatMap((item) => flattenTimedItems(item));
    if (typeof value !== 'object') return [];
    if (value.startTime && value.endTime) return [value];

    return [
        value.results,
        value.schedules,
        value.sessions,
        value.opportunities,
        value.dailySchedules,
        value.weeklySchedules,
        value.data,
    ].flatMap((candidate) => flattenTimedItems(candidate));
};

const isCanceledOrDeleted = (item: any): boolean => {
    if (!item || item.type === 'emptySchedule') return true;
    if (
        item.isDeleted === true ||
        item.deleted === true ||
        item.isCanceled === true ||
        item.isCancelled === true ||
        item.canceled === true ||
        item.cancelled === true ||
        item.deletedAt
    ) {
        return true;
    }

    const values = [item.status, item.sessionStatus, item.bookingStatus, item.state]
        .map((value) => asString(value).toUpperCase())
        .filter(Boolean);
    return values.some((value) => CANCELED_VALUES.has(value));
};

const getKind = (item: any, source: Source): SmartScheduleKind => {
    const type = asString(item.type).toLowerCase();
    const eventType = asString(item.eventType).toLowerCase();
    const scheduleType = asString(item.scheduleType).toLowerCase();
    const status = asString(item.status).toUpperCase();

    if (source === 'opportunity' || type === 'newopportunity' || eventType === 'newopportunity') {
        return 'opportunity';
    }
    if (type === 'request' || item.requestType || status === 'PENDING_APPROVAL') return 'request';
    if (type === 'block' || type === 'off' || scheduleType === 'off' || eventType === 'off' || status === 'OFF') return 'off';
    if (type === 'onetimeschedule' || type === 'general' || eventType === 'general') return 'general';
    // A coach can have multiple independent PT rows in the same time slot.
    // Only the dedicated group-session source may create a group event.
    if (source === 'group') return 'group';
    return 'private';
};

const getStatus = (item: any, kind: SmartScheduleKind, start: Dayjs, end: Dayjs, now: Dayjs): SmartScheduleStatus => {
    if (kind === 'request') return 'pending';
    if (kind === 'off') return 'off';

    const values = [item.attendanceStatus, item.status, item.bookingStatus]
        .map((value) => asString(value).toUpperCase())
        .filter(Boolean);
    if (values.some((value) => NO_SHOW_VALUES.has(value))) return 'noshow';
    if (values.includes('PENDING') || values.includes('WAITLISTED')) return 'pending';
    if (values.includes('COMPLETED') || values.includes('COMPLETE')) return 'completed';
    if ((now.isAfter(start) || now.isSame(start)) && now.isBefore(end)) return 'ongoing';
    if (now.isAfter(end) || now.isSame(end)) return 'completed';
    return 'upcoming';
};

const getMembers = (item: any): SmartScheduleMember[] => {
    const members: SmartScheduleMember[] = [];
    const addMember = (candidate: any, fallbackName = '') => {
        if (!candidate && !fallbackName) return;
        const id = getObjectId(candidate) || asString(candidate?.memberId);
        const name = firstString(candidate?.name, candidate?.memberName, candidate?.alias, fallbackName);
        if (!id && !name) return;
        if (members.some((member) => (id && member.id === id) || (!id && member.name === name))) return;
        members.push({ id, name });
    };

    const directMemberId = asString(item.memberId);
    const directMemberName = firstString(item.memberName, item.alias);
    if (item.member && typeof item.member === 'object') {
        addMember(
            {
                ...item.member,
                id: getObjectId(item.member) || directMemberId,
                name: firstString(item.member.name, directMemberName),
            },
            directMemberName
        );
    } else if (item.member || directMemberId || directMemberName) {
        addMember(item.member || { id: directMemberId, name: directMemberName }, directMemberName);
    }

    const rosterCandidates = [item.members, item.roster, item.reservations, item.bookings, item.attendees];
    rosterCandidates.forEach((roster) => {
        if (!Array.isArray(roster)) return;
        roster.forEach((entry: any) => {
            if (isCanceledOrDeleted(entry)) return;
            addMember(entry.member || entry, firstString(entry.memberName, entry.alias));
        });
    });

    return members;
};

const getMembershipName = (item: any): string =>
    firstString(
        item.membershipName,
        item.memberShipName,
        item.ticketName,
        item.membership?.name,
        item.memberShip?.name,
        item.ticket?.name
    );

const getSessionText = (item: any): string => {
    const current = firstFiniteNumber(item.currentSession, item.sessionCount, item.usedCount);
    const total = firstFiniteNumber(item.totalSession, item.totalCount);
    if (current === undefined && total === undefined) return '';
    return `${current ?? '?'}/${total ?? '?'}회`;
};

const getTitle = (item: any, kind: SmartScheduleKind, members: SmartScheduleMember[]): string => {
    if (kind === 'opportunity') {
        return firstString(item.title, item.name, item.categoryName, '신규기회');
    }
    if (kind === 'off') return firstString(item.title, item.name, 'OFF');
    if (kind === 'general') return firstString(item.name, item.title, '일반 일정');
    if (kind === 'request') return firstString(item.title, item.name, item.alias, item.memberName, '수업 신청');
    if (kind === 'group') return firstString(item.title, item.name, item.className, item.alias, '그룹 수업');
    return firstString(item.alias, item.memberName, item.title, item.name, members[0]?.name, 'PT');
};

const getSubtitle = (item: any, kind: SmartScheduleKind, members: SmartScheduleMember[]): string => {
    if (kind === 'opportunity') return firstString(item.memberName, item.alias, item.categoryName);
    if (kind === 'general') return firstString(item.memo, item.description);
    if (kind === 'request') return firstString(item.alias, item.memberName, item.requestType === 'change' ? '변경 요청' : '신청 대기');
    if (kind === 'group') return members.map((member) => member.name).filter(Boolean).join(', ');
    return members[0]?.name ? `${members[0].name} 회원` : '';
};

const buildSearchText = (event: Omit<SmartScheduleEvent, 'searchText'>): string =>
    [
        event.title,
        event.subtitle,
        event.coachName,
        ...event.memberNames,
        event.membershipName,
        event.sessionText,
        event.classType,
        ...event.rawItems.flatMap((item) => [item?.categoryName, item?.memo, item?.phoneNumber]),
    ]
        .filter(Boolean)
        .join(' ')
        .normalize('NFKC')
        .toLocaleLowerCase('ko-KR');

const normalizeEvent = (item: any, source: Source, index: number, now: Dayjs): PendingEvent | null => {
    if (isCanceledOrDeleted(item) || !item.startTime || !item.endTime) return null;

    const start = toSmartScheduleKst(item.startTime);
    const end = toSmartScheduleKst(item.endTime);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;

    const kind = getKind(item, source);
    const members = getMembers(item);
    const memberIds = members.map((member) => member.id).filter(Boolean);
    const memberNames = members.map((member) => member.name).filter(Boolean);
    const recordId = asString(
        item._id ||
            item.id ||
            item.scheduleId ||
            item.sessionId ||
            item.groupSessionId ||
            item.newOpportunityId
    );
    // 반복 OFF 는 서버(refineBlocks)가 회차마다 펼쳐 주지만 회차 모두가 원본 블록의
    // blockId 를 공유한다. blockId 만으로 키를 만들면 회차들이 한 장으로 합쳐지므로
    // 화면 키에는 시작·끝을 붙인다. 삭제·상세는 rawItems[0].blockId(원본)를 그대로 쓴다.
    const blockId = asString(item.blockId);
    const id =
        recordId ||
        (blockId
            ? `${blockId}:${start.valueOf()}:${end.valueOf()}`
            : `${kind}-${item.coachId || 'unknown'}-${start.valueOf()}-${end.valueOf()}-${index}`);
    const coachId = asString(item.coachId || item.trainerId || getObjectId(item.coach) || getObjectId(item.trainer));
    const coachName = firstString(item.coachName, item.trainerName, item.coach?.name, item.trainer?.name);
    const status = getStatus(item, kind, start, end, now);
    const classType = firstString(item.classType) || undefined;
    const currentCount = firstFiniteNumber(item.currentCount, item.reservationCount, item.bookedCount);
    const maxCapacity = kind === 'group'
        ? firstFiniteNumber(item.maxCapacity, item.capacity) ?? getFixedClassCapacity(classType)
        : undefined;
    const groupSessionId = kind === 'group'
        ? asString(item.groupSessionId || (source === 'group' ? item._id || item.id || item.sessionId : ''))
        : '';

    const eventWithoutSearch: Omit<SmartScheduleEvent, 'searchText'> = {
        id,
        kind,
        status,
        title: getTitle(item, kind, members),
        subtitle: getSubtitle(item, kind, members),
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        durationMinutes: getSmartScheduleDurationMinutes(start, end),
        coachId,
        coachName,
        members,
        memberIds,
        memberNames,
        membershipName: getMembershipName(item),
        sessionText: getSessionText(item),
        classType,
        groupSessionId: groupSessionId || undefined,
        currentCount,
        maxCapacity,
        isConfirmed: (kind === 'private' || kind === 'group') && status !== 'pending',
        rawItems: [item],
    };

    return {
        ...eventWithoutSearch,
        searchText: buildSearchText(eventWithoutSearch),
        source,
        sourcePriority: SOURCE_PRIORITY[source],
    };
};

const sessionKey = (event: PendingEvent): string => {
    // Time equality is not identity: concurrent PT rows must stay separate.
    return `${event.kind}:${event.id}`;
};

const mergeMembers = (left: SmartScheduleMember[], right: SmartScheduleMember[]): SmartScheduleMember[] => {
    const result: SmartScheduleMember[] = [];
    [...left, ...right].forEach((member) => {
        const duplicateIndex = result.findIndex(
            (existing) =>
                (member.id && existing.id === member.id) ||
                (Boolean(member.name) && existing.name === member.name && (!member.id || !existing.id))
        );
        if (duplicateIndex < 0) {
            result.push(member);
        } else if (!result[duplicateIndex].id && member.id) {
            result[duplicateIndex] = member;
        }
    });
    return result;
};

const rawIdentity = (item: any): string =>
    asString(item?._id || item?.id || item?.scheduleId || item?.sessionId || item?.newOpportunityId || '') ||
    `${item?.type || ''}:${item?.memberId || ''}:${item?.coachId || ''}:${item?.startTime || ''}:${item?.endTime || ''}`;

const mergeRawItems = (left: any[], right: any[]): any[] => {
    const result: any[] = [];
    const seen = new Set<string>();
    [...left, ...right].forEach((item) => {
        const identity = rawIdentity(item);
        if (identity && seen.has(identity)) return;
        if (identity) seen.add(identity);
        result.push(item);
    });
    return result;
};

const mergeStatus = (left: SmartScheduleStatus, right: SmartScheduleStatus): SmartScheduleStatus => {
    if (left === 'noshow' && right === 'noshow') return 'noshow';
    const statuses = [left, right].filter((status) => status !== 'noshow');
    if (statuses.includes('ongoing')) return 'ongoing';
    if (statuses.includes('upcoming')) return 'upcoming';
    if (statuses.includes('completed')) return 'completed';
    return statuses[0] || left;
};

const mergeEvents = (left: PendingEvent, right: PendingEvent): PendingEvent => {
    const preferred = right.sourcePriority > left.sourcePriority ? right : left;
    const other = preferred === right ? left : right;
    const members = mergeMembers(left.members, right.members);
    // rawItems[0] is consumed by the existing detail flows. Keep the richest
    // source first while retaining the member-level legacy rows behind it.
    const rawItems = mergeRawItems(preferred.rawItems, other.rawItems);

    if (
        (left.kind !== 'private' && left.kind !== 'group') ||
        (right.kind !== 'private' && right.kind !== 'group')
    ) {
        const memberIds = members.map((member) => member.id).filter(Boolean);
        const memberNames = members.map((member) => member.name).filter(Boolean);
        const mergedWithoutSearch: Omit<SmartScheduleEvent, 'searchText'> = {
            ...preferred,
            members,
            memberIds,
            memberNames,
            currentCount: Math.max(preferred.currentCount ?? 0, other.currentCount ?? 0, members.length) || undefined,
            maxCapacity: preferred.maxCapacity ?? other.maxCapacity,
            rawItems,
        };
        return {
            ...mergedWithoutSearch,
            searchText: buildSearchText(mergedWithoutSearch),
            source: preferred.source,
            sourcePriority: Math.max(left.sourcePriority, right.sourcePriority),
        };
    }

    const actualGroup = left.source === 'group' ? left : right.source === 'group' ? right : undefined;
    const inferredKind: SmartScheduleKind = actualGroup ? 'group' : 'private';
    const status = mergeStatus(left.status, right.status);
    const explicitClassType = [...preferred.rawItems, ...left.rawItems, ...right.rawItems]
        .map((item) => firstString(item?.classType))
        .find(Boolean);
    const inferredClassType = explicitClassType;
    const memberIds = members.map((member) => member.id).filter(Boolean);
    const memberNames = members.map((member) => member.name).filter(Boolean);
    const title =
        preferred.kind === 'group' && preferred.title
            ? preferred.title
            : inferredKind === 'group' && memberNames.length
            ? memberNames.join(', ')
            : preferred.title;
    const subtitle = inferredKind === 'group' ? memberNames.join(', ') : preferred.subtitle;

    const mergedWithoutSearch: Omit<SmartScheduleEvent, 'searchText'> = {
        ...preferred,
        kind: inferredKind,
        status,
        title,
        subtitle,
        members,
        memberIds,
        memberNames,
        membershipName: preferred.membershipName || left.membershipName || right.membershipName,
        sessionText: preferred.sessionText || left.sessionText || right.sessionText,
        classType: inferredClassType,
        currentCount: actualGroup?.currentCount,
        maxCapacity: actualGroup?.maxCapacity ?? getFixedClassCapacity(inferredClassType),
        isConfirmed: status !== 'pending',
        rawItems,
    };

    return {
        ...mergedWithoutSearch,
        searchText: buildSearchText(mergedWithoutSearch),
        source: preferred.source,
        sourcePriority: Math.max(left.sourcePriority, right.sourcePriority),
    };
};

const hasSameLessonSlot = (left: PendingEvent, right: PendingEvent): boolean =>
    Boolean(left.coachId)
    && left.coachId === right.coachId
    && dayjs(left.startTime).valueOf() === dayjs(right.startTime).valueOf()
    && dayjs(left.endTime).valueOf() === dayjs(right.endTime).valueOf();

const getGroupReferenceIds = (event: PendingEvent): string[] => Array.from(new Set([
    event.groupSessionId,
    ...event.rawItems.flatMap((item) => [
        item?.groupSessionId,
        item?.groupScheduleId,
        item?.sessionId,
    ]),
].map(asString).filter(Boolean)));

const findGroupRepresentationTarget = (
    rangeEvent: PendingEvent,
    groupEvents: PendingEvent[]
): number | undefined => {
    if (rangeEvent.source !== 'range') return undefined;

    const slotCandidates = groupEvents
        .map((event, index) => ({ event, index }))
        .filter(({ event }) => hasSameLessonSlot(rangeEvent, event));
    if (!slotCandidates.length) return undefined;

    const rangeReferences = new Set(getGroupReferenceIds(rangeEvent));
    if (rangeReferences.size) {
        const directlyLinked = slotCandidates.filter(({ event }) =>
            getGroupReferenceIds(event).some((id) => rangeReferences.has(id))
        );
        if (directlyLinked.length === 1) return directlyLinked[0].index;
        if (directlyLinked.length > 1) return undefined;
    }

    const rangeClassType = asString(rangeEvent.classType).toUpperCase();
    if (!GROUP_CLASS_TYPES.has(rangeClassType)) return undefined;
    const compatible = slotCandidates.filter(({ event }) => {
        const groupClassType = asString(event.classType).toUpperCase();
        return !groupClassType || groupClassType === rangeClassType;
    });
    // Ambiguous slot matches are deliberately left separate rather than risking
    // an unrelated PT disappearing into a group session.
    return compatible.length === 1 ? compatible[0].index : undefined;
};

const normalizeCoach = (coach: any, index: number): SmartScheduleCoach | null => {
    if (!coach || typeof coach !== 'object') return null;
    const id = asString(coach.coachId || coach.trainerId || coach._id || coach.id || coach.code || coach.value);
    if (!id || id === 'all') return null;
    return {
        id,
        name: firstString(
            coach.coachName,
            coach.trainerName,
            coach.name,
            coach.label,
            coach.value,
            coach.alias,
            coach.object?.name,
            '이름 없음'
        ),
        profileImageUrl: firstString(
            coach.coachProfileImageUrl,
            coach.profileImageUrl,
            coach.profileUrl,
            coach.imageUrl,
            coach.object?.profileImageUrl,
            coach.object?.coachProfileImageUrl
        ) || undefined,
        order: firstFiniteNumber(
            coach.order,
            coach.sequence,
            coach.sortOrder,
            coach.coachOrder,
            coach.object?.order,
            coach.object?.sequence,
            coach.object?.sortOrder,
            coach.object?.coachOrder
        ) ?? index,
        raw: coach,
    };
};

const buildCoachRoster = (coachesInput: any, events: SmartScheduleEvent[]): SmartScheduleCoach[] => {
    const result: SmartScheduleCoach[] = [];
    const seen = new Set<string>();
    unwrapCollection(coachesInput).forEach((coach, index) => {
        const normalized = normalizeCoach(coach, index);
        if (!normalized || seen.has(normalized.id)) return;
        seen.add(normalized.id);
        result.push(normalized);
    });

    events.forEach((event) => {
        if (!event.coachId || seen.has(event.coachId)) return;
        seen.add(event.coachId);
        result.push({
            id: event.coachId,
            name: event.coachName || '이름 없음',
            order: result.length,
            raw: event.rawItems[0],
        });
    });

    return result.sort((left, right) => left.order - right.order || left.name.localeCompare(right.name, 'ko'));
};

export const normalizeSmartScheduleData = ({
    rangeSchedules,
    groupSessions,
    newOpportunities,
    coaches,
    now = new Date(),
}: NormalizeSmartScheduleInput): NormalizedSmartScheduleData => {
    const normalizedNow = toSmartScheduleKst(now);
    const pendingEvents = [
        ...flattenTimedItems(rangeSchedules).map((item, index) => normalizeEvent(item, 'range', index, normalizedNow)),
        ...flattenTimedItems(groupSessions).map((item, index) => normalizeEvent(item, 'group', index, normalizedNow)),
        ...flattenTimedItems(newOpportunities).map((item, index) => normalizeEvent(item, 'opportunity', index, normalizedNow)),
    ].filter((event): event is PendingEvent => Boolean(event));

    const merged = new Map<string, PendingEvent>();
    pendingEvents.forEach((event) => {
        const key = sessionKey(event);
        const existing = merged.get(key);
        merged.set(key, existing ? mergeEvents(existing, event) : event);
    });

    const deduplicated = Array.from(merged.values());
    const groupEvents = deduplicated.filter((event) => event.source === 'group');
    const consumedRangeEvents = new Set<PendingEvent>();
    deduplicated.forEach((event) => {
        const targetIndex = findGroupRepresentationTarget(event, groupEvents);
        if (targetIndex === undefined) return;
        groupEvents[targetIndex] = mergeEvents(groupEvents[targetIndex], event);
        consumedRangeEvents.add(event);
    });
    const resolved = [
        ...deduplicated.filter((event) => event.source !== 'group' && !consumedRangeEvents.has(event)),
        ...groupEvents,
    ];

    const events: SmartScheduleEvent[] = resolved
        .map(({ source: _source, sourcePriority: _sourcePriority, ...event }) => event)
        .sort(
            (left, right) =>
                dayjs(left.startTime).valueOf() - dayjs(right.startTime).valueOf() ||
                left.coachName.localeCompare(right.coachName, 'ko') ||
                left.id.localeCompare(right.id)
        );

    return {
        events,
        coaches: buildCoachRoster(coaches, events),
    };
};
